const ConsoleHelper = require('../core-helpers/console-helper.js');

/**
 * Utils.js - busuu helper
 *
 * A tiny Vanilla JavaScript library
 * to provide handy shortcuts in a front-end developer day
 *
 */
const Utils = (function() {


    /**
     * Indicates wether or not variable is an Array
     * @param  {ANY_TYPE} variable
     * @return {Boolean}
     * @public
     */
    function isArray(variable) {
        return Array.isArray(variable);
    }

    /**
     * Indicates wether or not variable is an Object
     * @param  {ANY_TYPE} variable
     * @return {Boolean}
     * @public
     */
    function isObject(variable) {
        return Object.prototype.toString.call(variable) === '[object Object]';
    }

    /**
     * Convert a Node list or HTMLCollection to a real Array
     * @param  {Collection}
     * @return {Array}
     * @private
     */
    function toArray(collection) {
        const newArray = [];
        if (!collection) {
            return newArray;
        }
        for (let i = 0, l = collection.length; i < l; i++) {
            newArray.push(collection[i]);
        }
        return newArray;
    }

    /**
     * Returns a given id element
     * @param  {String}
     * @return {Element || null}
     * @public
     */
    function getById(id) {
        return document.getElementById(id) || null;
    }

    /**
     * Returns elements based on a given classname
     * @param  {String} selector
     * @param  {Element} parentNode
     * @return {Array}
     * @public
     */
    function getElementsByClass(selector, parent = document) {
        const elements = parent.querySelectorAll(`.${selector}`);
        return toArray(elements);
    }

    /**
     * Returns element based on a given classname
     * @param  {String} selector
     * @param  {Element} parentNode
     * @return {Element}
     * @public
     */
    function getElementByClass(selector, parent = document) {
        return parent.querySelector(`.${selector}`) || null;
    }

    /**
     * Returns elements based on a given attribute
     * @param  {String} attribute
     * @param  {Element} parentNode
     * @return {Array}
     * @public
     */
    function getElementsByAttribute(attribute, parent = document) {
        const elements = parent.querySelectorAll(`[${attribute}]`);
        return toArray(elements);
    }

    /**
     * Returns element based on a given attribute
     * @param  {String} attribute
     * @param  {Element} parentNode
     * @return {Element}
     * @public
     */
    function getElementByAttribute(attribute, parent = document) {
        return parent.querySelector(`[${attribute}]`) || null;
    }

    /**
     * Trigger custom event, cross browser compatible
     * createEvent and initEvent are deprecated but working on IE11
     */
    const triggerEvent = (eventName, eventTarget, toBubble = false) => {
        let event = null;
        if (typeof (Event) === 'function') {
            event = new Event(eventName, { bubbles: toBubble });
        } else {
            event = document.createEvent('Event');
            event.initEvent(eventName, true, true);
        }
        eventTarget.dispatchEvent(event);
    };

    /**
     * React behaves differently when it comes to setting a value in a text input.
     * Referenced from: https://github.com/facebook/react/issues/10135#issuecomment-314441175
     * The answer is set in the element:
     * - For React: checks the existence of the 'value' property & setter
     * - Otherwise: sets the value directly
     */
    const setNativeValue = (element, value) => {
        const valueProperty = Object.getOwnPropertyDescriptor(element, 'value');
        if (valueProperty) {
            const valueSetter = Object.getOwnPropertyDescriptor(element, 'value').set;
            const prototype = Object.getPrototypeOf(element);
            const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value').set;

            if (valueSetter && valueSetter !== prototypeValueSetter) {
                prototypeValueSetter.call(element, value);
            } else {
                valueSetter.call(element, value);
            }
        } else {
            element.value = value;
        }
    };

    /**
     * Find the closest parent element based on a className
     * up to the document. Returns null if not found.
     * @param  {string} className
     * @param  {element} child
     */
    function getParentByClass(className, child) {
        const parent = child.parentNode;
        if (parent === document) {
            return null;
        }
        if (hasClass(parent, className)) {
            return parent;
        }
        return getParentByClass(className, parent);
    }

    /**
     * DOM Manipulation Shortcuts
     * @private
     */
    function _forEachClass(array, callback) {
        array = array.split(' ');
        for (let i = 0, l = array.length; i < l; i++) {
            callback(array[i], i);
        }
    }

    /**
     * Check an existing class on a given element
     * @param  {Element}  elem
     * @param  {String}   className
     * @return {Boolean}
     * @public
     */
    function hasClass(elem, className) {
        return elem && elem.classList && elem.classList.contains(className);
    }

    /**
     * Add a class on element (multiples classes supported)
     * @param {Element} elem
     * @param {String} classes
     * @public
     */
    function addClass(elem, classes) {
        _forEachClass(classes, (className) => {
            elem.classList.add(className);
        });
    }

    /**
     * Remove a class on element (multiples classes supported)
     * @param  {Element} elem
     * @param  {String}  classes
     * @public
     */
    function removeClass(elem, classes) {
        _forEachClass(classes, (className) => {
            elem.classList.remove(className);
        });
    }

    /**
     * Toggle a class on element (multiples classes supported)
     * @param  {Element} elem
     * @param  {String}  classes
     * @public
     */
    function toggleClass(elem, classes) {
        _forEachClass(classes, (className) => {
            elem.classList.toggle(className);
        });
    }

    /**
     * Display a warning message in the console
     * @param {String} fileName    - file name where log is coming from
     * @param {String} message     - warning to display
     * @param {Multiple} datas     - can be any additional datas (optional)
     * @param {Function} callback  - (optional)
     * @public
     */
    function log(fileName, message, datas, callback) {
        ConsoleHelper.log(fileName, message, datas, callback);
    }

    /**
     * Convert a Timestamp to 'DD/MM/YYYY' or 'HH:MM DD/MM/YYYY'
     * @param  {int} timestamp
     * @param  {Boolean} withTime true if you want to have the time
     * @return {String}
     * @public
     */
    function convertTimestamp(timestamp, withTime) {
        let d = new Date(timestamp),
            yyyy = d.getFullYear(),
            mm = (`0${d.getMonth() + 1}`).slice(-2), // Months are zero based. Add leading 0.
            dd = (`0${d.getDate()}`).slice(-2); // Add leading 0.

        if (withTime) {
            let hh = (`0${d.getHours()}`).slice(-2); // Add leading 0.
            let min = (`0${d.getMinutes()}`).slice(-2); // Add leading 0.
            return `${hh}:${min} ${dd}/${mm}/${yyyy}`;
        }

        return `${dd}/${mm}/${yyyy}`;
    }

    /**
     * A boolean to check if env is localhost
     * @return {Boolean}
     * @public
     */
    function isLocalEnvironment() {
        const hostname = document.location.hostname;
        const locals = ['localhost', 'local.internal.busuu.com', 'local-hp.internal.busuu.com'];
        return Boolean(locals.indexOf(hostname) !== -1);
    }

    /**
     * A boolean to check if the current
     * environment is production.
     * (www.busuu.com || org.busuu.com || slate.internal.busuu.com)
     * @return {boolean}
     */
    function isProduction() {
        const currentHostname = document.location.hostname;
        const hostnames = ['www.busuu.com', 'org.busuu.com', 'slate.internal.busuu.com'];
        return hostnames.some((hostname) => hostname === currentHostname);
    }

    /**
     * Clean multiples white spaces and line breaks from a given string
     * @param  {String}
     * @return {String}
     * @public
     */
    function clean(str) {
        return str.trim().replace(/\s+/g, ' ');
    }

    /**
     * Toggle show/hide an element
     * @param  {Element} elem
     * @public
     */
    function toggleShow(elem) {
        if (elem.style.display === 'block') {
            elem.style.display = 'none';
        } else {
            elem.style.display = 'block';
        }
    }

    /**
     * Hide an element using CSS
     * @param  {Element} elem
     * @public
     */
    function hide(elem) {
        elem.style.display = 'none';
    }

    /**
     * Show an element using css
     * @param  {Element} elem
     * @public
     */
    function show(elem) {
        elem.style.display = 'block';
    }

    /**
     * Return the text value from an element without html
     * @param  {Element} - elem
     * @return {String}
     * @public
     */
    function getText(elem) {
        return elem.textContent || elem.innerText;
    }

    /**
     * Set text value on a given element
     * @param  {Element} - elem
     * @return {String} - text
     * @public
     */
    function setText(elem, text) {
        if (elem.textContent) {
            elem.textContent = text;
        } else if (elem.innerText) {
            elem.innerText = text; // IE
        } else {
            const _text = document.createTextNode(text);
            elem.appendChild(_text);
        }
    }

    /**
     * Return a parameter value from the current URL
     * @param  {String} - parameter
     * @param {String} - key to use in location object (search/hash etc...)
     * @return {String || null}
     * @public
     * NB: We have a key switcher for Angular as there's a # in the URL the search value is always empty
     */
    function getParameter(param, key = 'search') {

        // Get search portion of url without `?`
        let query = location[key].split('?')[1];
        if (query) {

            // Create array with all parameters in form of variable=value
            let parameters = query.split('&');

            for (let i = 0, l = parameters.length; i < l; i++) {
                /**
                 * Split the parameter so the first index contains the name
                 * of variable and the second one the value
                 */
                let currentParameter = parameters[i].split('=');
                if (currentParameter[0] === param) {
                    return currentParameter[1]; // Return parameter value
                }
            }
        }

        return null; // Return null when not found
    }

    /**
     * Parse query after HP redirects to Webapp with reversed query position
     * e.g ?course=course_pack_en_travel&lang=en#/redirect/select_course
     * returns course=course_pack_en_travel&lang=en
     * @param {String} href
     */
    const getQuery = (href) => {
        const removeHash = (query) => {
            const queryParts = query.split('#');
            return queryParts.length ? queryParts[0] : query;
        };

        const linkParts = href && href.split('?');
        const query = linkParts && linkParts.length >= 2 ? removeHash(linkParts[1]) : null;
        return query;
    };

    /**
     * Delegates an Event on a given Element
     * @param  {Element}   - elem handler
     * @param  {String}    - className target
     * @param  {Event}     - evt name
     * @param  {Function}  - callback
     * @public
     */
    function delegate(elem, className, evt, callback) {
        elem.addEventListener(evt, (e) => {
            let _target = e.target;
            while (_target != elem) {
                if (hasClass(_target, className)) {
                    callback.call(_target, e);
                    break;
                }
                _target = _target.parentNode;
            }
        });
    }

    /**
     * Format a given number to readable human format
     * e.g.: 1 > 1.00
     *       -1 > -1.00
     *       10 > 10.00
     *       99.85 > 99.85
     * @param  {Integer || String}
     * @return {String}
     * @public
     */
    function formatNumber(num) {
        let i = parseFloat(num);
        if (isNaN(i)) {
            i = 0.00;
        }
        let s = i.toString();
        if (s.indexOf('.') < 0) {
            s += '.00';
        }
        if (s.indexOf('.') == (s.length - 2)) {
            s += '0';
        }
        return s.toString();
    }

    /**
     * Recursively clone an Object or an Array.
     * Does a deep cloning and avoid object reference.
     * @param  {Object || Array} - collection
     * @return {Object || Array}
     * @public
     */
    function deepClone(collection) {
        const cloneObject = function(object) {
            const obj = {};
            for (const prop in object) {
                if (object.hasOwnProperty(prop)) {
                    const value = object[prop];
                    if (isObject(value) || isArray(value)) {
                        obj[prop] = deepClone(value);
                    } else {
                        obj[prop] = value;
                    }
                }
            }
            return obj;
        };

        const cloneArray = function(array) {
            const arr = [];
            for (let i = 0, l = array.length; i < l; i++) {
                const value = array[i];
                if (isObject(value) || isArray(value)) {
                    arr.push(deepClone(value));
                } else {
                    arr.push(value);
                }
            }
            return arr;
        };

        if (isObject(collection)) {
            return cloneObject(collection);
        } else if (isArray(collection)) {
            return cloneArray(collection);
        }

        throw new Error('\'collection\' argument must be an Object or an Array');
    }

    /**
     * Return the caret position inside a given element
     * @param  {Element} element - DOM element (input, textarea)
     * @return {Integer}
     * @public
     */
    function getCaretPosition(element) {
        if (!element) {
            return 0;
        }

        if ('selectionStart' in element) {
            return element.selectionStart;
        } else if (document.selection) { // IE
            element.focus();
            const sel = document.selection.createRange();
            const selLen = sel.text.length;
            sel.moveStart('character', -element.value.length);
            return sel.text.length - selLen;
        }

        return 0;
    }

    /**
     * Set the caret position inside a given element
     * @param  {Element} element - DOM element (input, textarea)
     * @return {Integer}
     * @public
     */
    function setCaretPosition(element, position) {
        if (!element) {
            return false;
        }

        if (element.createTextRange) {
            const range = element.createTextRange();
            range.move('character', position);
            range.select();
        } else if (element.selectionStart) {
            element.focus();
            setTimeout(() => {
                element.setSelectionRange(position, position);
            }, 0);
        } else {
            element.focus();
        }
    }

    /**
     * Check if the given parent element has the given child as descendant
     * @param {Element} parent
     * @param {Element} child
     * @public
     */
    function hasChild(parent, child) {
        let parentChild = child.parentNode;
        while (parentChild !== null) {
            if (parentChild === parent) {
                return true;
            }
            parentChild = parentChild.parentNode;
        }
        return false;
    }

    /**
     * Indicates whether or not a given
     * value is a finite number
     * @param  {all} value
     * @return {Boolean}
     */
    function _isFinite(value) {
        return (typeof value === 'number') && window.isFinite(value);
    }

    /**
     * Indicates whether or not a given
     * value is a number
     * @param  {all} value
     * @return {Boolean}
     */
    function isNumber(value) {
        return _isFinite(value) && !isNaN(value);
    }

    /**
     * Will check if the value is numeric (nb: 1 or '1' will return true)
     * @param {String|Number} value
     * @return {Boolean}
     */
    function isNumeric(value) {
        return window.isFinite(value) && !window.isNaN(value);
    }

    /**
     * Returns user timezone offset from UTC based on internal clock
     * We default to zero hours (UTC) if this fails
     * NB: getTimezoneOffset always returns the 'inverted'
     * value of what we'd expect, eg:
     * - PST (GMT - 8) returns 480 minutes (8 hours behind GMT)
     * - NZDT (GMT + 13) returns -780 minutes (13 hours ahead of GMT)
     * We want the opposite in hours, so we always divide our timezone by 60 and multiply it by -1.
     * @return {Integer} timezone offset
     */
    function getTimezoneOffsetFromUTC() {
        const date = new Date();
        const timezone = date.getTimezoneOffset() || 0;

        if (timezone === 0) {
            return timezone;
        }

        return (timezone / 60) * -1;
    }

    /**
     * The API expects our timezone offset to be formatted in
     * a specific way: (+/-)00:00
     * @param {Integer} timezone
     * @return {String} timezone in format (+/-)00:00
     */
    function formatTimezoneOffset(timezone) {

        if (!isNumber(timezone)) {
            throw new Error('timezone value must be a number');
        }

        let hours = null;
        let minutes = null;
        const timezoneIsNegative = Boolean(timezone < 0);

        if (timezone === 0) {
            return '+00:00';
        }

        // If timezone is negative, multiply by -1 to always set a positive timezone
        if (timezoneIsNegative) {
            timezone *= -1;
        }

        // Set hours
        if (timezone > 0 && timezone < 10) {
            hours = `0${Math.floor(timezone)}`;
        } else {
            hours = Math.floor(timezone);
        }

        // Set minutes - get any decimals
        const decimals = timezone - Math.floor(timezone);
        switch (decimals) {
            case 0.25:
                minutes = '15';
                break;
            case 0.5:
                minutes = '30';
                break;
            case 0.75:
                minutes = '45';
                break;
            default:
                minutes = '00';
        }

        // Append (+/-) based on whether original timezone was positive or negative
        hours = (timezoneIsNegative) ? `-${hours}` : `+${hours}`;

        return `${hours}:${minutes}`;
    }

    /**
     * Converts accents from a given string to their
     * closest sibling.
     * Example: 'Crème brulée à chaud ça régale' > 'Creme brulee a chaud ca regale'
     * @param {string} string
     * @return {string}
     */
    function convertAccents(string) {
        const normalizedString = string.normalize('NFD');
        const valueWithoutSpecialCharacters = normalizedString.replace(/[\u0300-\u036f]/g, '');
        return valueWithoutSpecialCharacters;
    }

    /**
     * Removes special characters from a given string
     * Example:
     * - removeSpecialCharacters('Crème_Brul(ée) {à} chaud ç@-régale!') > 'CrmeBrulechaudrgale'
     * - removeSpecialCharacters('Crème_Brul(ée) {à} chaud ç@-régale!', 'è_ç@-') > 'Crème_Brulechaudç@-rgale'
     * NB: Preserve words from A to Z but consider accents as special characters.
     * @param {string} string
     * @param {string} exceptions - A string with special characters whitelist
     * @return {string}
     */
    function removeSpecialCharacters(string, exceptions = '') {

        /**
         * Ensure the exceptions doesn't have any special
         * Regexp characters so we escape them using `\`
         */
        exceptions = exceptions.split('').join('\\');

        // Create regexp replacement rule
        const replace = `[^A-Za-z${exceptions}]`;

        // Return string without special characters
        const regexp = new RegExp(replace, 'g');
        return string.replace(regexp, '');

    }

    /**
     * Takes an element from a given index in an array
     * and moves it to the end of the given array.
     * @param  {Array} array
     * @param  {Index} index
     * @return {Array}
     */
    function moveToEndOfArray(array, index = 0) {
        const newArray = deepClone(array);
        const removedEl = newArray.splice(index, 1)[0];
        newArray.push(removedEl);
        return newArray;
    }

    return {
        toArray,
        isArray,
        isObject,
        getById,
        getElementsByClass,
        getElementByClass,
        getElementsByAttribute,
        getElementByAttribute,
        triggerEvent,
        getParentByClass,
        hasClass,
        addClass,
        removeClass,
        toggleClass,
        log,
        convertTimestamp,
        isLocalEnvironment,
        isProduction,
        clean,
        toggleShow,
        hide,
        show,
        getText,
        setText,
        getParameter,
        getQuery,
        delegate,
        formatNumber,
        deepClone,
        getCaretPosition,
        setCaretPosition,
        hasChild,
        isFinite: _isFinite,
        isNumber,
        isNumeric,
        getTimezoneOffsetFromUTC,
        formatTimezoneOffset,
        convertAccents,
        removeSpecialCharacters,
        moveToEndOfArray,
        setNativeValue
    };

}());

module.exports = Utils;
